/* Video qui ne se lance pas : THE LAB et THE SCAN n'ont pas la meme regle (13/10/2026).
 *
 * THE LAB : l'apprenant ne doit jamais etre enferme (B44), la video se libere.
 * THE SCAN : decision d'Hicham du 13/10. On ne saute plus jamais une video : un
 * candidat qui bloque la lecture sautait la mise en situation, repondait aux
 * quiz sans l'avoir vue, et rien ne le signalait au recruteur (test du 13/10,
 * 4 IA sur 4). Le candidat recharge la page, puis ecrit au support si cela
 * continue ; la reprise le ramene sur la meme video (indexDeReprise).
 *
 * `utilisateur` : getCurrentUser(). Meme critere que le reste du lecteur
 * (UserContext) pour reconnaitre un candidat THE SCAN. */
export const estCandidatScan = (utilisateur) => utilisateur?.type === 'theScanCandidat'

export const peutLibererVideoEnPanne = (utilisateur) => !estCandidatScan(utilisateur)

/* ─── 100 % de la video, THE SCAN (decision d'Hicham, 13/10/2026) ───────────────────────────────
 * Pour un candidat, « Suivant » ne s'ouvre que lorsque CHAQUE seconde de la video a ete lue pour de
 * vrai (lecture normale : les sauts ne comptent pas), puis que le serveur a confirme l'annonce. Plus
 * de tolerance a 99 % ni a 50 % a la fin (elles restent pour THE LAB). Le serveur refuse « fait »
 * sans cette annonce (formationController.js, video_vue).
 * ⚠️ L'annonce est declaree par le navigateur : elle arrete un saut direct, pas un script qui forge
 * aussi le signal. */
export const creerSuiviLecture = () => {
  const vues = new Set()
  const secondesTotal = (duree) => Math.max(1, Math.floor(Number(duree) || 0))
  return {
    marquer(secondes) { const s = Math.floor(Number(secondes)); if (Number.isFinite(s) && s >= 0) vues.add(s) },
    /* Le lecteur a atteint la fin naturellement : la derniere seconde, que les mesures peuvent manquer, compte. */
    marquerLaFin(duree) { if (Number(duree) > 0) vues.add(secondesTotal(duree) - 1) },
    secondesVues(duree) { const n = secondesTotal(duree); let c = 0; for (let i = 0; i < n; i++) if (vues.has(i)) c++; return c },
    complet(duree) { return Number(duree) > 0 && this.secondesVues(duree) >= secondesTotal(duree) },
    total: secondesTotal,
    vider() { vues.clear() },
  }
}

/* Annonce « video lue en entier » au serveur, avec quelques essais. Rend true seulement si le serveur
   l'a enregistree (ou l'avait deja : plafond atteint). `envoyer(moduleID, evenements)` = APIS.signauxScan. */
export const annoncerVideoVue = async (envoyer, moduleID, sequenceID, secondes, { tentatives = 3, attenteMs = 2000, attendre = (ms) => new Promise((r) => setTimeout(r, ms)) } = {}) => {
  const evenement = { type: 'video_vue', sequenceID, valeur: secondes, detail: { total: secondes } }
  for (let i = 0; i < tentatives; i++) {
    let r = null
    try { r = await envoyer(moduleID, [evenement]) } catch (e) { r = null }
    if (r && !r.error && (r.ecrits > 0 || r.plein === true)) return true
    if (i < tentatives - 1) await attendre(attenteMs)
  }
  return false
}

/* Ce que le NAVIGATEUR dit avoir lu (13/10/2026). L'element video garde lui-meme la liste exacte des passages
 * reellement lus (`played`, des TimeRanges) : un saut en cree un trou, et surtout il ne depend PAS de nos mesures
 * toutes les 0,4 s, que le navigateur ralentit (onglet en arriere-plan, machine chargee). Sans cela, un candidat
 * honnete dont les mesures ont ete ralenties se retrouvait bloque devant « regardez la video en entier ».
 * Rend `null` si le navigateur ne sait pas le dire (alors seules nos mesures comptent), sinon
 * { complet, duree }. Tolerance de `tolerance` seconde au debut et a la fin ; deux passages separes de moins de
 * 0,5 s sont un meme passage (les pauses et reprises laissent de petits ecarts d'arrondi). */
export const passagesLusParLeNavigateur = (video, tolerance = 1) => {
  try {
    const duree = Number(video?.duration)
    const p = video?.played
    if (!p || !(duree > 0) || !Number.isFinite(duree) || typeof p.length !== 'number' || p.length === 0) return null
    const passages = []
    for (let i = 0; i < p.length; i++) passages.push([p.start(i), p.end(i)])
    passages.sort((a, b) => a[0] - b[0])
    const fusion = [passages[0].slice()]
    for (const [debut, fin] of passages.slice(1)) {
      const dernier = fusion[fusion.length - 1]
      if (debut - dernier[1] <= 0.5) dernier[1] = Math.max(dernier[1], fin)
      else fusion.push([debut, fin])
    }
    return { complet: fusion.length === 1 && fusion[0][0] <= tolerance && fusion[0][1] >= duree - tolerance, duree }
  } catch (e) {
    return null
  }
}
